const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
const LS_KEY = 'lux_demo_subjects';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeLocal(list) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch {}
}

function toLocalSubject(form) {
  const now = new Date().toISOString();
  return {
    id_materia: `local-${Date.now()}`,
    nombre: form.nombre ?? '',
    tipo: form.tipo ?? '',
    institucion: form.institucion ?? '',
    academia: form.academia ?? '',
    campus: form.campus ?? '',
    clave: form.clave ?? '',
    seccion: form.seccion ?? '',
    descripcion: form.descripcion ?? '',
    creditos: form.creditos ? Number(form.creditos) : null,
    objetivos: form.objetivos ?? '',
    horas_docentes_semana: form.horasDocentesSemana ? Number(form.horasDocentesSemana) : null,
    horas_totales_semana: form.horasTotalesSemana ? Number(form.horasTotalesSemana) : null,
    prerrequisitos: form.prerrequisitos ?? '',
    estado: form.estado ?? 'ACTIVO',
    creado_en: now,
    _local: true,
  };
}

// Backend mapping helpers
function mapApiToRow(api) {
  // Map backend materia to UI row shape consumed by SubjectsTable
  const tipoMap = {
    'INSTITUCIONAL': 'Institucional',
    'COMPARTIDA': 'Compartida',
    'ESPECIFICA': 'Específica'
  };
  return {
    id_materia: api.id_materia,
    id: api.id_materia, // Para compatibilidad
    nombre: api.nombre_materia || '—',
    tipo: (api.tipo_materia ? (tipoMap[api.tipo_materia] || api.tipo_materia) : '—'),
    tipo_raw: api.tipo_materia || '—',
    academia: api.academia_nombre || '—',
    academia_nombre: api.academia_nombre || '—',
    campus: api.campus || '—', // Si no existe, muestra guion
    seccion: api.seccion_nombre || '—',
    seccion_nombre: api.seccion_nombre || '—',
    institucion: api.institucion_nombre || '—',
    institucion_nombre: api.institucion_nombre || '—',
    clave: api.clave_materia || '—',
    creditos: (typeof api.creditos !== 'undefined' && api.creditos !== null) ? api.creditos : '—',
    horas_frente_asesor: (typeof api.horas_frente_asesor !== 'undefined' && api.horas_frente_asesor !== null) ? api.horas_frente_asesor : '—',
    horas_totales_semana: (typeof api.horas_totales_semana !== 'undefined' && api.horas_totales_semana !== null) ? api.horas_totales_semana : '—',
    descripcion: api.descripcion || '—',
    objetivos: api.objetivos || '—',
    estado: (typeof api.activo !== 'undefined' && api.activo !== null) ? (api.activo ? 'ACTIVO' : 'INACTIVO') : '—',
    // IDs para edición
    id_institucion: api.id_institucion || null,
    id_seccion: api.id_seccion || null,
    id_academia: api.id_academia || null,
  };
}

function mapFormToApiPayload(form) {
  // Convert UI modal form to backend fields; PATCH-friendly (send only present values)
  const toInt = (v) => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : undefined;
  };
  // Map tipo UI to backend enum if user uses labels
  const tipoUi = form.tipo;
  const tipoBackend = tipoUi === 'Básica' ? 'obligatoria'
    : tipoUi === 'Especializada' ? 'optativa'
    : (tipoUi || undefined);
  const payload = {
    nombre_materia: form.nombre ?? undefined,
    tipo_materia: tipoBackend,
    codigo_materia: form.clave ?? undefined,
    creditos: toInt(form.creditos),
    horas_teoria: toInt(form.horasDocentesSemana),
    horas_practica: toInt(form.horasTotalesSemana),
    periodo_sugerido: toInt(form.periodoSugerido),
    estado: form.estado ? String(form.estado).toLowerCase() : undefined,
  };
  // Remove undefined keys to keep PATCH minimal
  return Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined));
}

export async function getSubjects({ page = 1, pageSize = 20, search = '' } = {}) {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('page_size', pageSize);
  const trimmedSearch = String(search || '').trim();
  if (trimmedSearch) {
    params.append('search', trimmedSearch);
  }
  try {
    const res = await fetch(`${API_BASE}/escolar/materias/?${params.toString()}`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al obtener materias');
    const data = await res.json();
    // Soportar dos formatos de respuesta:
    // 1) Objeto paginado: { count, results: [...], next, previous }
    // 2) Lista simple: [ ... ]

    let rawResults;
    let count = null;
    let nextPage = null;
    let previous = null;

    if (Array.isArray(data)) {
      // Respuesta como lista simple
      rawResults = data;
      count = data.length;
    } else {
      // Respuesta paginada estándar de DRF
      rawResults = Array.isArray(data.results) ? data.results : [];
      count = typeof data.count === 'number' ? data.count : rawResults.length;
      nextPage = data.next ? page + 1 : null;
      previous = data.previous ?? null;
    }

    const results = Array.isArray(rawResults) ? rawResults.map(mapApiToRow) : [];

    return {
      count,
      results,
      nextPage,
      previous,
      raw: data,
    };
  } catch (err) {
    console.error('[subjects.js] Error al cargar materias:', err);
    return { count: 0, results: [], next: null, previous: null };
  }
}

/**
 * Obtiene TODAS las materias del backend sin paginación.
 * Útil para dropdowns donde se necesita mostrar todas las opciones.
 * Hace múltiples requests si es necesario para obtener todas las páginas.
 */
export async function getAllSubjects() {
  const allResults = [];
  let page = 1;
  const pageSize = 200; // Tamaño grande para minimizar requests
  let hasMore = true;

  try {
    while (hasMore) {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);

      const res = await fetch(`${API_BASE}/escolar/materias/?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (!res.ok) throw new Error('Error al obtener materias');
      const data = await res.json();

      let rawResults;
      if (Array.isArray(data)) {
        rawResults = data;
        hasMore = false; // Si es array simple, no hay paginación
      } else {
        rawResults = Array.isArray(data.results) ? data.results : [];
        hasMore = data.next !== null;
      }

      // Mapear y agregar resultados
      const mappedResults = rawResults.map(mapApiToRow);
      allResults.push(...mappedResults);

      page++;

      // Seguridad: máximo 50 páginas para evitar loops infinitos
      if (page > 50) {
        console.warn('[subjects.js] getAllSubjects: Se alcanzó el límite de 50 páginas');
        break;
      }
    }

    console.log(`[subjects.js] getAllSubjects: Total materias cargadas: ${allResults.length}`);
    return allResults;
  } catch (err) {
    console.error('[subjects.js] Error al cargar todas las materias:', err);
    return [];
  }
}

export async function createSubject(form) {
  // Map UI form to backend expected field names
  const toInt = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : null;
  };
  const toDecimal = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
  };
  
  // Map tipo_materia UI labels to backend ENUM values
  const tipoUi = form.subjectType || form.tipo;
  const tipoBackend = tipoUi === 'Institucional' ? 'INSTITUCIONAL'
    : tipoUi === 'Compartida' ? 'COMPARTIDA'
    : tipoUi === 'Específica' ? 'ESPECIFICA'
    : (tipoUi || 'INSTITUCIONAL');
  
  // Validar campos obligatorios antes de enviar
  const id_institucion = toInt(form.institution || form.institucion);
  const id_seccion = toInt(form.section || form.seccion);
  const horas_frente_asesor = toDecimal(form.duration || form.horasDocentesSemana) || 0;
  
  if (!id_institucion) {
    throw new Error('La institución es obligatoria');
  }
  if (!id_seccion) {
    throw new Error('La sección es obligatoria');
  }
  
  const payload = {
    nombre_materia: form.name || form.nombre || '',
    clave_materia: form.rvoe || form.clave || '',
    tipo_materia: tipoBackend,
    horas_frente_asesor: horas_frente_asesor,
    horas_totales_semana: toDecimal(form.horasTotalesSemana),
    descripcion: form.description || form.descripcion || '',
    objetivos: form.objectives || form.objetivos || '',
    activo: form.status === 'ACTIVO' || form.estado === 'ACTIVO' || form.estado === undefined,
    id_institucion: id_institucion,
    id_seccion: id_seccion,
    id_academia: toInt(form.academia) || null,
  };
  
  console.log('[subjects.js] Payload para crear materia:', payload);
  
  try {
    const res = await fetch(`${API_BASE}/escolar/materias/`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const error = await res.json();
      console.error('[subjects.js] Error del backend:', error);
      throw new Error(JSON.stringify(error));
    }
    const created = await res.json();
    console.log('[subjects.js] Materia creada exitosamente:', created);
    // Mapear la respuesta al formato de UI para que se renderice correctamente
    return mapApiToRow(created);
  } catch (err) {
    console.error('[subjects.js] Error al crear materia:', err);
    throw err;
  }
}

export async function getSubject(id) {
  const res = await fetch(`${API_BASE}/escolar/materias/${id}/`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error('Error al obtener la materia');
  const api = await res.json();
  
  console.log('[subjects.js] Materia obtenida del backend:', api);
  
  // Mapear tipo_materia del backend a etiqueta UI
  const tipoMap = {
    'INSTITUCIONAL': 'Institucional',
    'COMPARTIDA': 'Compartida',
    'ESPECIFICA': 'Específica'
  };
  
  // Extraer ID: puede venir como número directo o como objeto con id
  const extractId = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object' && val !== null) {
      return String(val.id || val.id_institucion || val.id_seccion || val.id_academia || '');
    }
    return String(val);
  };
  
  return {
    id_materia: api.id_materia,
    nombre: api.nombre_materia || '',
    tipo: tipoMap[api.tipo_materia] || api.tipo_materia || '',
    institucion: extractId(api.id_institucion),
    institucion_nombre: api.institucion_nombre || '',
    academia: extractId(api.id_academia),
    academia_nombre: api.academia_nombre || '',
    campus: '',
    clave: api.clave_materia || '',
    seccion: extractId(api.id_seccion),
    seccion_nombre: api.seccion_nombre || '',
    descripcion: api.descripcion || '',
    creditos: api.creditos ?? '',
    objetivos: api.objetivos || '',
    horasDocentesSemana: api.horas_frente_asesor ?? '',
    horasTotalesSemana: api.horas_totales_semana ?? '',
    prerrequisitos: '',
    estado: api.activo ? 'ACTIVO' : 'INACTIVO',
  };
}

export async function updateSubject(id, form) {
  // Validar que el ID existe
  if (!id) {
    throw new Error('ID de materia no proporcionado');
  }
  
  const toInt = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : null;
  };
  const toDecimal = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
  };
  
  // Mapear tipo UI a backend ENUM
  const tipoUi = form.tipo;
  const tipoBackend = tipoUi === 'Institucional' ? 'INSTITUCIONAL'
    : tipoUi === 'Compartida' ? 'COMPARTIDA'
    : tipoUi === 'Específica' ? 'ESPECIFICA'
    : (tipoUi || undefined);
  
  const payload = {
    nombre_materia: form.nombre || undefined,
    clave_materia: form.clave || undefined,
    tipo_materia: tipoBackend,
    horas_frente_asesor: toDecimal(form.horasDocentesSemana),
    horas_totales_semana: toDecimal(form.horasTotalesSemana),
    descripcion: form.descripcion || undefined,
    objetivos: form.objetivos || undefined,
    activo: form.estado === 'ACTIVO',
    id_institucion: toInt(form.institucion),
    id_seccion: toInt(form.seccion),
    id_academia: toInt(form.academia),
  };
  
  // Limpiar campos undefined
  const cleanPayload = Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined)
  );
  
  console.log('[subjects.js] Actualizando materia ID:', id, 'Payload:', cleanPayload);
  
  const res = await fetch(`${API_BASE}/escolar/materias/${id}/`, {
    method: 'PATCH',
    headers: { 
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify(cleanPayload),
  });
  
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    console.error('[subjects.js] Error al actualizar:', error);
    throw new Error(JSON.stringify(error) || 'Error al actualizar la materia');
  }
  
  const updated = await res.json();
  console.log('[subjects.js] Materia actualizada:', updated);
  return updated;
}

export async function deleteSubject(id) {
  const res = await fetch(`${API_BASE}/escolar/materias/${id}/`, { 
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    let message = 'No se puede eliminar la materia.';
    try {
      const errorData = await res.json();
      if (errorData && (errorData.detail || errorData.message || errorData.error)) {
        message = errorData.detail || errorData.message || errorData.error;
      }
    } catch (_) {}
    throw new Error(message);
  }
  return true;
}

export async function getAcademias() {
  try {
    const res = await fetch(`${API_BASE}/catalogos/academias/`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) return [];
    const data = await res.json();
    // Manejar respuesta paginada o array directo
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.results)) {
      return data.results;
    }
    return [];
  } catch (err) {
    console.error('[getAcademias] Error:', err);
    return [];
  }
}

/**
 * Obtener TODAS las academias (manejando paginación)
 */
export async function getAllAcademias() {
  const allAcademias = [];
  let page = 1;
  const pageSize = 100;
  let hasMore = true;
  
  try {
    while (hasMore) {
      const res = await fetch(`${API_BASE}/catalogos/academias/?page=${page}&page_size=${pageSize}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) break;
      const data = await res.json();
      
      // Manejar respuesta paginada o array directo
      if (Array.isArray(data)) {
        allAcademias.push(...data);
        hasMore = false; // No hay paginación
      } else if (data && Array.isArray(data.results)) {
        allAcademias.push(...data.results);
        hasMore = !!data.next;
        page++;
      } else {
        hasMore = false;
      }
    }
  } catch (err) {
    console.error('[getAllAcademias] Error:', err);
  }
  
  return allAcademias;
}
